//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { QueryRenderer, ReadyState } from "react-relay";
// chrome-day imports
import { queries } from "./ChromeDayRenderer.gql";
import { IRendererProps, IRendererContainer, IRendererState } from "./ChromeDay.d";
import { ChromeDay } from "./ChromeDay";
// other internals
import { IReduxState } from "@state/reducers/root.reducer";
import { CalloutBox } from "@components/callout-box";
import { setLastOffline } from "@state/actions/day-view.actions";
// util
import { getCurrentTimeZone } from "@util/time-zone.util";
import { handleReadyStateError } from "@util/relay.util";

//////////////////
// COMPONENT
//////////////////
class ChromeDayRendererWrapper extends React.PureComponent<IRendererProps, IRendererState> {
  private staleViewer: ISchema.IViewer | undefined;
  private shouldForce: boolean;

  constructor(props: IRendererProps) {
    super(props);

    this.state = {
      lastForceKey: 0,
    };
  }

  private setShouldForceUpdate() {
    this.props.setLastOffline(new Date().valueOf());

    this.shouldForce = true;
    this.setState({
      lastForceKey: this.state.lastForceKey + 1,
    });
  }

  public componentDidMount() {
    const dvLastOffline = this.props.dvLastOffline;

    if (dvLastOffline < this.props.lastOffline && this.props.isOnline) {
      this.setShouldForceUpdate();
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IRendererProps) {
    const dvLastOffline = nextProps.dvLastOffline;

    if (
      (nextProps.selectedMoment.clone().startOf("week").format("YYYY-MM-DD") !==
        this.props.selectedMoment.clone().startOf("week").format("YYYY-MM-DD") ||
        dvLastOffline < nextProps.lastOffline) &&
      nextProps.isOnline
    ) {
      this.setShouldForceUpdate();
    }
  }

  public render() {
    const weekStart = this.props.selectedMoment.clone().startOf("week");

    const weekStartDates = [weekStart.format("YYYY-MM-DD")];

    return (
      <QueryRenderer
        key={this.state.lastForceKey}
        environment={this.props.environment}
        query={queries}
        variables={{
          weekStartDates,
          dates: new Array(7)
            .fill(null)
            .map((_d, i) => weekStart.clone().add(i, "days").format("YYYY-MM-DD")),
          calendarIds: this.props.calendarIds,
          timeZone: getCurrentTimeZone(),
        }}
        cacheConfig={{ force: this.shouldForce }}
        render={(readyState: ReadyState) => {
          // check for error or show a loader for first load
          if (readyState.error) {
            handleReadyStateError(readyState.error, "ChromeDayRenderer");

            return (
              <CalloutBox error>An error has occurred. Please try refreshing the page.</CalloutBox>
            );
          } else if (readyState.props) {
            // fetch complete
            this.shouldForce = false;
            this.staleViewer = readyState.props.viewer;
            return (
              <ChromeDay
                calendarIds={this.props.calendarIds}
                viewer={readyState.props.viewer}
                fastCrawlFinished={this.props.fastCrawlFinished}
                onDayEventClick={this.props.onDayEventClick}
                weekStartDates={weekStartDates}
              />
            );
          } else if (!!this.staleViewer) {
            // fetch incomplete
            return (
              <ChromeDay
                calendarIds={this.props.calendarIds}
                viewer={this.staleViewer}
                loading={true}
                fastCrawlFinished={this.props.fastCrawlFinished}
                onDayEventClick={this.props.onDayEventClick}
                weekStartDates={weekStartDates}
              />
            );
          } else {
            return (
              <ChromeDay
                calendarIds={this.props.calendarIds}
                viewer={null}
                loading={true}
                fastCrawlFinished={this.props.fastCrawlFinished}
                onDayEventClick={this.props.onDayEventClick}
                weekStartDates={weekStartDates}
              />
            );
          }
        }}
      />
    );
  }
}

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IRendererContainer) {
  return {
    selectedMoment: state.dayView.selectedMoment,
    lastOffline: state.webExtension.lastOffline,
    isOnline: state.webExtension.isOnline,
    dvLastOffline: state.dayView.lastOffline,
    fastCrawlFinished: state.onboarding.fastCrawlFinished,
  };
}

export const ChromeDayRenderer = connect(mapStateToProps, {
  setLastOffline,
})(ChromeDayRendererWrapper);
